/**
 * Tipos e chamadas de "Meu perfil".
 *
 * O contrato espelha `api/internal/modulos/conta/perfil.go`. É a conta de quem
 * está logado, e só dela: nenhuma destas chamadas leva identificador de
 * usuário, porque o servidor tira o id da SESSÃO. Se o id viesse daqui,
 * qualquer sessão válida editaria o perfil de quem quisesse.
 *
 * Arquivo separado de `empresa.ts` de propósito: são dois recursos com donos
 * diferentes — a loja é da gestão, o perfil é da pessoa —, e um arquivo só
 * faria a tela de empresa importar o tipo de senha.
 */
import { api } from './api'

export type Perfil = {
  nome: string
  /** É a credencial de login. Não se troca por aqui — ver `salvarPerfil`. */
  email: string
  email_confirmado: boolean
  papel: string
  papel_rotulo: string
  /** A linha "para quem serve este perfil" da spec 04. */
  papel_descricao: string
  iniciais: string
  ultimo_acesso_em?: string
  criado_em: string
}

/**
 * O corpo do PUT.
 *
 * Só o nome. O e-mail é a credencial de login e precisa de confirmação antes
 * de valer: a API recusa a troca com 422, e a tela diz isso em vez de exibir
 * um campo que não salva.
 *
 * A função (papel) também não está aqui: quem muda papel é a gestão de
 * usuários, não a própria pessoa.
 */
export type PedidoPerfil = {
  nome: string
}

export type PedidoSenha = {
  senha_atual: string
  nova_senha: string
  repetir_senha: string
}

export type ResultadoSenha = {
  mensagem: string
  /**
   * Quantos OUTROS acessos caíram.
   *
   * A pessoa precisa ver o número: é assim que ela percebe que havia uma
   * sessão aberta que não era dela — o motivo mais comum de trocar a senha às
   * pressas.
   */
  sessoes_encerradas: number
  expira_em: string
}

export function buscarPerfil(sinal?: AbortSignal): Promise<Perfil> {
  return api.get<Perfil>('/perfil', sinal)
}

export function salvarPerfil(pedido: PedidoPerfil): Promise<Perfil> {
  return api.put<Perfil>('/perfil', pedido)
}

/**
 * Troca a senha conferindo a atual.
 *
 * O servidor derruba todas as sessões e devolve um cookie novo para este
 * navegador — quem trocou a própria senha continua dentro, e todo o resto cai.
 */
export function trocarSenha(pedido: PedidoSenha): Promise<ResultadoSenha> {
  return api.post<ResultadoSenha>('/perfil/senha', pedido)
}

/**
 * O corpo de PUT /perfil/pin — define ou troca o PIN de 4 dígitos que
 * autoriza ação de terceiro no balcão (spec 04, migração 0005).
 *
 * Exige a senha atual, a mesma exigência de segurança da troca de senha: o
 * PIN libera desconto e ajuste de estoque, e trocá-lo sem provar a senha
 * deixaria uma sessão esquecida aberta redefinir a chave.
 */
export type PedidoPIN = {
  senha_atual: string
  pin: string
  repetir_pin: string
}

export type ResultadoPIN = {
  mensagem: string
}

/**
 * Troca o PIN conferindo a senha atual.
 *
 * Ao contrário de `trocarSenha`, não devolve cookie novo nem encerra sessão:
 * o PIN nunca abre sessão, só autoriza uma ação específica no momento em que
 * é digitado (spec 04).
 */
export function trocarPIN(pedido: PedidoPIN): Promise<ResultadoPIN> {
  return api.put<ResultadoPIN>('/perfil/pin', pedido)
}
